/**
 * The order's answers, offered inside the form that continues it.
 *
 * A child form's workflow runs with the parent submission beside it — every
 * step can read `{{parent.salesRepEmail}}` and a branch can test
 * `parent.orderType` — and until now the only way to use that was to know it
 * and type it. This is the picker's half: the parents' published questions,
 * keyed `parent.<key>`, arranged so they sit in every picker beside the
 * form's own.
 *
 * One child can continue several parents (one "Location details" behind every
 * partner's order form), so the questions are the UNION by key. A key some
 * parents lack is still offered — the row works for the orders that have it
 * and reads blank for the rest — and says which forms lack it, because a
 * column that is blank for one partner's orders is a support call unless
 * somebody was told.
 *
 * A parent can continue a form of its own (order → location details →
 * field-ops dispatch). Each parent arrives with the forms IT continues under
 * `parents`, and their questions are offered one level further back as
 * `parent.parent.<key>` — the dispatch reads the order's company name without
 * the form in between asking for it again. Every function here walks those
 * levels, so a picker that offers the parents offers the whole chain.
 */
import type { FieldDefinition, FormLayout, FormWidget } from '../../shared/types';
import { asksNothing, flattenWidgetTree } from '../../shared/types';
import { OptGroup, SecInfo, groupsForContainer, plainOption, sectionsFromLayout, withSubs } from './fieldGroups';

/** One form this one continues, with what a picker needs of it. */
export interface ParentForm {
  id: number;
  name: string;
  status: string;
  fields: FieldDefinition[];
  layout?: Pick<FormLayout, 'sections'> | null;
  /** Its repeating containers, whose rows a template reaches by looping — see `parentLoopGroups`. */
  widgets?: FormWidget[];
  /** The forms this parent continues in turn — one level further back. */
  parents?: ParentForm[];
}

/** The prefix every parent token carries — the `parent` scope the server runs with. */
export const PARENT_PREFIX = 'parent.';

/** Answers a parent can hand across: main-form questions that ask something. */
const carryable = (f: FieldDefinition) =>
  f.scope === 'order' && !asksNothing(f.input_type) && f.input_type !== 'file';

/**
 * The forms `parents` continue, once each — the next level back. Two location
 * forms that both continue the same order name that order once.
 */
export function ancestorsOf(parents: ParentForm[]): ParentForm[] {
  const out = new Map<number, ParentForm>();
  for (const p of parents) for (const q of p.parents || []) if (!out.has(q.id)) out.set(q.id, q);
  return [...out.values()];
}

const BACK = ['', '', 'two forms back', 'three forms back', 'four forms back'];
/** How far back a level is, in words: level 2 is the form a parent continued. */
const backWords = (level: number) => BACK[level] || `${level} forms back`;

/** One level's questions, keyed `prefix<key>`. */
function levelFieldDefs(parents: ParentForm[], prefix: string, level: number): FieldDefinition[] {
  const out: FieldDefinition[] = [];
  const seen = new Set<string>();
  for (const p of parents) {
    for (const f of p.fields) {
      if (!carryable(f) || seen.has(f.key)) continue;
      seen.add(f.key);
      const lacking = parents.filter((q) => !q.fields.some((g) => g.key === f.key)).map((q) => q.name);
      // Further back, the label names the form holding it: "Company name"
      // alone would read as the parent's own question.
      const label = level > 1 ? `${f.label} — from "${p.name}"` : f.label;
      out.push({
        ...f,
        key: `${prefix}${f.key}`,
        label: lacking.length ? `${label} — not on ${lacking.join(', ')}` : label,
      });
    }
  }
  return out;
}

/**
 * The parents' questions as ONE list of field definitions, keyed `parent.<key>`
 * — and then the forms before them, keyed `parent.parent.<key>`, and so on.
 *
 * The definition itself is the first parent's — its label and, for a
 * dropdown, its options, so a branch on `parent.orderType` offers the
 * order's own choices. Every picker that takes `FieldDefinition[]` for its
 * lookups (`describeGroup`, the value dropdown of a condition) can be handed
 * these beside the form's own fields and read `parent.orderType` as
 * "Order type — the order".
 */
export function parentFieldDefs(parents: ParentForm[], prefix = PARENT_PREFIX, level = 1): FieldDefinition[] {
  if (!parents.length) return [];
  return [
    ...levelFieldDefs(parents, prefix, level),
    ...parentFieldDefs(ancestorsOf(parents), `${prefix}${PARENT_PREFIX}`, level + 1),
  ];
}

/**
 * The parents' questions as picker groups, keyed `parent.<key>` (or bare,
 * for the Access tab's carry rows, which name the parent's key itself).
 *
 * One parent: arranged the way that form is built, under its name. Several:
 * one group, since three forms' sections would be three arrangements of one
 * list. Composite answers add their parts (`parent.shipTo.city`), which the
 * server reads to any depth.
 *
 * The forms further back follow, the same way, under a heading that says how
 * far back they are — last, so a picker opens on the nearest form.
 */
export function parentOptGroups(
  parents: ParentForm[], opts: { prefix?: string; subs?: boolean } = {}, level = 1
): OptGroup[] {
  const prefix = opts.prefix ?? PARENT_PREFIX;
  const expand = opts.subs === false ? plainOption : withSubs;
  const re = (g: OptGroup): OptGroup => ({ ...g, opts: g.opts.map((o) => ({ v: `${prefix}${o.v}`, l: o.l })) });
  if (!parents.length) return [];
  const back = level > 1 ? `, ${backWords(level)}` : '';
  const deeper = parentOptGroups(ancestorsOf(parents), { ...opts, prefix: `${prefix}${PARENT_PREFIX}` }, level + 1);
  if (parents.length === 1) {
    const p = parents[0];
    const sections: SecInfo[] = sectionsFromLayout(p.layout);
    const fields = p.fields.filter(carryable);
    return [
      ...groupsForContainer('order', fields, sections, expand, {
        prefix: `From "${p.name}"${back}`, strayLabel: sections.length ? 'Other fields' : 'Questions',
      }).map(re),
      ...deeper,
    ];
  }
  const defs = levelFieldDefs(parents, '', 1);
  return [
    {
      label: `From the ${level > 1 ? `forms ${backWords(level)}` : 'order'} (${parents.map((p) => `"${p.name}"`).join(', ')})`,
      opts: defs.flatMap(expand).map((o) => ({ v: `${prefix}${o.v}`, l: o.l })),
    },
    ...deeper,
  ];
}

/** The same, in the connection editors' `{ key, label }` shape. */
export function parentTokenGroups(parents: ParentForm[]): { label: string; options: { key: string; label: string }[] }[] {
  return parentOptGroups(parents).map((g) => ({ label: g.label, options: g.opts.map((o) => ({ key: o.v, label: o.l })) }));
}

/**
 * Which of these parents lack `key` — for a carry row's warning. A key from
 * further back (`parent.companyName`) is lacking on a parent when none of the
 * forms IT continues has it.
 */
export function parentsLacking(parents: ParentForm[], key: string): ParentForm[] {
  if (key.startsWith(PARENT_PREFIX)) {
    const rest = key.slice(PARENT_PREFIX.length);
    return parents.filter((p) => {
      const up = p.parents || [];
      return parentsLacking(up, rest).length === up.length;
    });
  }
  const head = key.split('.')[0];
  return parents.filter((p) => !p.fields.some((f) => f.key === head));
}

/**
 * The files uploaded on the forms before this one, as the tokens a note or a
 * ticket body lists them with: `parent.attachmentLinks` and its neighbours,
 * then `parent.parent.…` for the form before that. The server fills them per
 * level from every upload on that submission (`parentContextOf`).
 *
 * Only a level that asks for a file is offered — and, unlike an answer, a
 * file question INSIDE a repeating container counts, since the tokens gather
 * the whole submission's.
 */
export function parentFileTokens(
  parents: ParentForm[], prefix = PARENT_PREFIX, level = 1
): { key: string; label: string }[] {
  if (!parents.length) return [];
  const asking = parents.filter((p) => p.fields.some((f) => f.input_type === 'file'));
  // Several parents ask: a submission continues exactly one of them, so
  // these are "the parent form's" files — not "the order's", which reads as
  // a particular form when there are three.
  const on = asking.length === 1
    ? `"${asking[0].name}"${level > 1 ? `, ${backWords(level)}` : ''}`
    : `the ${level > 1 ? `forms ${backWords(level)}` : 'parent form'}`;
  return [
    ...(asking.length ? [
      { key: `${prefix}attachmentLinks`, label: `Links to the files uploaded on ${on}` },
      { key: `${prefix}attachmentNames`, label: `Names of the files uploaded on ${on}` },
      { key: `${prefix}attachmentCount`, label: `How many files were uploaded on ${on}` },
    ] : []),
    ...parentFileTokens(ancestorsOf(parents), `${prefix}${PARENT_PREFIX}`, level + 1),
  ];
}

// ---------------------------------------------------------------------------
// The parents' repeating containers
// ---------------------------------------------------------------------------

/** A picker option that may write a whole block — the connection editors' `TokenOption`. */
interface LoopOption { key: string; label: string; insert?: string }
interface LoopGroup { label: string; options: LoopOption[] }

/** One repeating level of the parents, with the loop that reaches it. */
interface ParentLevel {
  label: string;
  /** Every `{{#each}}` it takes to stand inside one row, outermost first. */
  open: string[];
  /** The questions asked per row, the union by key across the parents. */
  fields: FieldDefinition[];
}

/**
 * The repeating levels of one generation of parents, outermost first.
 *
 * A list on the order ("additional contacts to copy in") is an array in the
 * order's answers, so it is `parent.<scope>` to the server and reachable only
 * by `{{#each parent.<scope>}}`; a level nested inside it is that row's own
 * `<scope>`, bare, one loop further in. Parents are matched by the path of
 * scopes down to a level: three partner order forms built from one template
 * share it and are offered once, and a question some of them lack says so,
 * the way a main-form question does.
 */
function parentLevels(parents: ParentForm[], prefix: string): ParentLevel[] {
  const out = new Map<string, ParentLevel>();
  for (const p of parents) {
    const nodes = (p.widgets || []).flatMap((w) => (w.tree ? flattenWidgetTree(w.tree) : []));
    const byScope = new Map(nodes.map((n) => [n.scope, n]));
    const has = (q: ParentForm, scope: string, key: string) => q.fields.some((g) => g.scope === scope && g.key === key);
    for (const n of nodes) {
      const path: string[] = [];
      for (let cur: typeof n | undefined = n; cur; cur = cur.parentScope ? byScope.get(cur.parentScope) : undefined) {
        path.unshift(cur.scope);
      }
      const id = path.join('/');
      const level = out.get(id) || {
        label: n.itemLabel?.trim() || n.scope,
        open: path.map((s, i) => `{{#each ${i ? '' : prefix}${s}}}`),
        fields: [],
      };
      out.set(id, level);
      for (const f of p.fields) {
        if (f.scope !== n.scope || asksNothing(f.input_type) || f.input_type === 'file') continue;
        if (level.fields.some((g) => g.key === f.key)) continue;
        const lacking = parents.filter((q) => !has(q, n.scope, f.key)).map((q) => q.name);
        level.fields.push(lacking.length ? { ...f, label: `${f.label} — not on ${lacking.join(', ')}` } : f);
      }
    }
  }
  return [...out.values()];
}

/** Where a generation of parents' answers come from, as a group heading says it. */
function sourceLabel(parents: ParentForm[], level: number): string {
  if (parents.length === 1) return `From "${parents[0].name}"${level > 1 ? `, ${backWords(level)}` : ''}`;
  return `From the ${level > 1 ? `forms ${backWords(level)}` : 'order'} (${parents.map((p) => `"${p.name}"`).join(', ')})`;
}

/**
 * The parents' repeating levels, for a template BODY: the loop itself, opened
 * and closed with a blank line to write in, then the questions that only mean
 * something inside it. The body-side twin of the connection editors' own
 * "Repeat a block" — without it a list on the order could be used in a child
 * form's ticket only by somebody who knew to type `{{#each parent.item1}}`.
 */
export function parentLoopGroups(parents: ParentForm[], prefix = PARENT_PREFIX, level = 1): LoopGroup[] {
  if (!parents.length) return [];
  const levels = parentLevels(parents, prefix);
  const from = sourceLabel(parents, level);
  return [
    ...(levels.length ? [{
      label: `${from} › Repeat a block`,
      options: levels.map((l) => ({
        key: `each-${l.open.join('')}`,
        label: `For every ${l.label}…`,
        insert: `${l.open.join('')}\n  \n${l.open.map(() => '{{/each}}').join('')}`,
      })),
    }] : []),
    ...levels.map((l) => ({
      label: `${from} › ${l.label} — use inside ${l.open.join('')}`,
      options: [
        ...l.fields.flatMap(withSubs).map((o) => ({ key: o.v, label: o.l })),
        { key: 'index', label: `${l.label} number (1, 2, 3…)` },
        { key: 'total', label: `How many ${l.label}s` },
      ],
    })),
    ...parentLoopGroups(ancestorsOf(parents), `${prefix}${PARENT_PREFIX}`, level + 1),
  ];
}

/**
 * The same levels for an ADDRESS line, which has no blank middle to write in:
 * each option is the finished loop — that answer from every row — and the
 * server's address clean-up drops the trailing comma, the blanks and the
 * repeats. Email questions are offered; a level that asks for none falls back
 * to its plain text questions, as the form's own levels do.
 */
export function parentAddressLoopGroups(parents: ParentForm[], prefix = PARENT_PREFIX, level = 1): LoopGroup[] {
  if (!parents.length) return [];
  const from = sourceLabel(parents, level);
  return [
    ...parentLevels(parents, prefix).flatMap((l) => {
      const typed = l.fields.filter((f) => f.input_type === 'email');
      const pool = typed.length ? typed : l.fields.filter((f) => f.input_type === 'text');
      if (!pool.length) return [];
      const open = l.open.join('');
      return [{
        label: `${from} › Every ${l.label} — one address from each`,
        options: pool.flatMap(plainOption).map((o) => ({
          key: `each-${open}-${o.v}`,
          label: o.l,
          insert: `${open}{{${o.v}}}, ${l.open.map(() => '{{/each}}').join('')}`,
        })),
      }];
    }),
    ...parentAddressLoopGroups(ancestorsOf(parents), `${prefix}${PARENT_PREFIX}`, level + 1),
  ];
}

/** Both, as a connection editor takes them — undefined when the parents repeat nothing. */
export function parentLoopsOf(parents: ParentForm[]): { body: LoopGroup[]; address: LoopGroup[] } | undefined {
  const body = parentLoopGroups(parents);
  return body.length ? { body, address: parentAddressLoopGroups(parents) } : undefined;
}
